import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckCircle2, Circle, UserX, Users } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { formatDate } from "../lib/format";
import { ATTENDANCE_LABEL, PAYROLL_LABEL, PAYROLL_TONE } from "../lib/people";
import type { EmployeeFull, LeaveRequest, PayrollRun } from "../api/types";

/**
 * The owner's My Desk: how the business is operating, not a manager's task queue.
 *
 * Every figure is read from the same endpoints the modules own, so a number
 * here can never disagree with the list it opens. Routine attendance follow-ups
 * do not appear -- those belong to the reporting manager's desk. Leave
 * decisions do: the owner is the decider for a manager's request and for a
 * request with no reporting line, so those are surfaced with their buttons.
 */
export function OwnerDeskPage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const currency = user?.account?.currency ?? "INR";
  const today = useMemo(() => localDate(timezone), [timezone]);
  const monthPrefix = today.slice(0, 7);

  const employees = useQuery({
    queryKey: ["owner-desk-employees"],
    queryFn: ({ signal }) => api.employeeList({ page_size: 200, ordering: "last_name" }, signal),
    staleTime: 5 * 60_000,
  });
  const attendance = useQuery({
    queryKey: ["owner-desk-attendance", today],
    queryFn: ({ signal }) => api.attendancePage({ date: today, page_size: 200 }, signal),
  });
  const shifts = useQuery({
    queryKey: ["owner-desk-shifts", today],
    queryFn: ({ signal }) => api.shiftsPage({ from: today, to: today, page_size: 200 }, signal),
  });
  const pendingLeave = useQuery({
    queryKey: ["owner-desk-leave-pending"],
    queryFn: ({ signal }) => api.leaveRequests({ status: "pending", page_size: 200 }, signal),
  });
  const payrollRuns = useQuery({
    queryKey: ["owner-desk-payroll"],
    queryFn: ({ signal }) => api.payrollRuns({ page_size: 12 }, signal),
  });

  const queryClient = useQueryClient();
  const decideLeave = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      action === "approve" ? api.approveLeaveRequest(id) : api.rejectLeaveRequest(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["owner-desk-leave-pending"] }),
  });

  const pending =
    employees.isPending || attendance.isPending || shifts.isPending || payrollRuns.isPending;
  const error = employees.error || attendance.error || shifts.error || payrollRuns.error;

  const active = (employees.data?.results ?? []).filter((e) => e.status !== "exited");
  const scheduled = (shifts.data?.results ?? []).filter((s) => !s.is_off);
  const attendanceRows = attendance.data?.results ?? [];
  const present = attendanceRows.filter(
    (row) => row.status === "present" || row.status === "half_day",
  );
  const onLeave = attendanceRows.filter((row) => row.status === "on_leave");

  const scheduledIds = new Set(scheduled.map((shift) => shift.employee));
  const checkedInIds = new Set(present.map((row) => row.employee));
  const noShow = [...scheduledIds].filter((id) => !checkedInIds.has(id));

  const byBranch = countBy(
    active,
    (e) => e.branch_name || (e.is_account_owner ? "Owner / unassigned" : "No branch assigned"),
  );
  const byDepartment = countBy(
    active,
    (e) =>
      e.department_name || (e.is_account_owner ? "Owner / unassigned" : "No department assigned"),
  );
  const noManager = active.filter((e) => !e.reporting_to_name && !e.is_account_owner);
  const emptyDepartments = [...(employees.data?.results ?? [])]
    .map((e) => e.department_name)
    .filter(Boolean);
  const joinersThisMonth = active.filter((e) => (e.joining_date ?? "").startsWith(monthPrefix));

  const currentRun = (payrollRuns.data?.results ?? [])[0];

  if (error) {
    const planLimit = error instanceof ApiError && error.isPlanLimit;
    return (
      <>
        <PageHeader title="My Desk" subtitle="How the business is operating." />
        <EmptyState
          title={planLimit ? "Plan limit reached" : "Some figures could not be loaded"}
          body={
            planLimit
              ? `${error.message} Attendance, roster and payroll come with the Growth plan.`
              : error instanceof ApiError
                ? error.message
                : "Please try again."
          }
          action={
            planLimit ? (
              <Link to="/settings/plan" className="text-sm font-medium text-slate-900 hover:underline">
                See plans →
              </Link>
            ) : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="My Desk"
        subtitle={`Business overview for ${formatDate(today, timezone)}.`}
      />

      <SetupChecklist employees={employees.data?.results ?? []} />

      <QuickLinks />

      <Snapshot
        loading={pending}
        items={[
          {
            key: "headcount",
            label: "Active employees",
            value: active.length,
            to: "/people/employees",
            icon: <Users size={15} />,
          },
          {
            key: "scheduled",
            label: "Scheduled today",
            value: scheduled.length,
            to: "/people/roster",
            icon: <CalendarDays size={15} />,
          },
          {
            key: "present",
            label: "Present today",
            value: present.length,
            to: "/people/attendance",
            icon: <Users size={15} />,
          },
          {
            key: "gap",
            label: "Scheduled, not in",
            value: noShow.length,
            to: "/people/attendance",
            icon: <UserX size={15} />,
          },
        ]}
      />

      <div className="mb-6">
        <OwnerLeaveDecisions
          rows={pendingLeave.data?.results ?? []}
          loading={pendingLeave.isPending}
          pending={decideLeave.isPending}
          onDecide={(id, action) => decideLeave.mutate({ id, action })}
        />
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <WorkforceCard
          title="Headcount by branch"
          loading={employees.isPending}
          counts={byBranch}
          total={active.length}
        />
        <WorkforceCard
          title="Headcount by department"
          loading={employees.isPending}
          counts={byDepartment}
          total={active.length}
        />

        <Card>
          <h2 className="mb-3 text-sm font-semibold text-slate-900">Payroll period</h2>
          {payrollRuns.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : !currentRun ? (
            <EmptyState
              title="No payroll run"
              body="Open a run under Payroll to pay this period."
              action={
                <Link to="/people/payroll" className="text-sm font-medium text-slate-900 hover:underline">
                  Go to Payroll →
                </Link>
              }
            />
          ) : (
            <PayrollPeriodCard run={currentRun} currency={currency} />
          )}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
            Needs your attention
            <Link to="/people/reports" className="text-xs font-normal text-slate-500 hover:text-slate-700">
              Reports →
            </Link>
          </h2>
          <ul className="divide-y divide-slate-100">
            {currentRun && (currentRun.status === "draft" || currentRun.status === "computed") ? (
              <AttentionRow
                label="Payroll period not finalised"
                detail={`${currentRun.run_number} is ${PAYROLL_LABEL[currentRun.status ?? "draft"]} — review and approve`}
                count={1}
                to="/people/payroll"
              />
            ) : null}
            <AttentionRow
              label="Employees with no reporting manager"
              detail="Their leave routes to the owner instead"
              count={noManager.length}
              to="/people/employees"
            />
            <AttentionRow
              label="Pending leave requests"
              detail="With the reporting managers, and with you where the rules say so"
              count={pendingLeave.data?.results.length ?? 0}
              to="/people/leave?status=pending"
            />
            <AttentionRow
              label="New joiners this month"
              detail="Confirm probation and documents"
              count={joinersThisMonth.length}
              to="/people/employees"
            />
            <AttentionRow
              label="Scheduled but not checked in today"
              detail="Confirm cover or regularize"
              count={noShow.length}
              to="/people/attendance"
            />
          </ul>
          {emptyDepartments.length === 0 && !employees.isPending ? (
            <p className="mt-3 text-xs text-slate-400">
              No departments configured yet. Set them up under Organisation.
            </p>
          ) : null}
        </Card>

        <Card>
          <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
            Today&apos;s leave
            <Link to="/people/leave" className="text-xs font-normal text-slate-500 hover:text-slate-700">
              Leave →
            </Link>
          </h2>
          {attendance.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : onLeave.length === 0 ? (
            <EmptyState title="No one on leave today" body="Approved leave appears here." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {onLeave.slice(0, 6).map((row) => (
                <li key={row.id} className="flex items-center justify-between py-2">
                  <Link
                    to={`/people/employees/${row.employee}`}
                    className="text-sm text-slate-800 hover:underline"
                  >
                    {row.employee_name}
                  </Link>
                  <Badge tone="sky">{ATTENDANCE_LABEL[row.status ?? "on_leave"]}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

/**
 * The leave waiting on the owner.
 *
 * The owner decides a manager's request and any request whose reporting line
 * points nowhere; the rest sit with the reporting manager. Only the rows the
 * server says this caller may decide are offered with buttons.
 */
function OwnerLeaveDecisions({
  rows,
  loading,
  pending,
  onDecide,
}: {
  rows: LeaveRequest[];
  loading: boolean;
  pending: boolean;
  onDecide: (id: string, action: "approve" | "reject") => void;
}) {
  const decidable = rows.filter((row) => row.can_decide);

  return (
    <Card>
      <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
        Leave waiting for you
        <Link to="/people/leave?status=pending" className="text-xs font-normal text-slate-500 hover:text-slate-700">
          All pending →
        </Link>
      </h2>
      {loading ? (
        <Skeleton className="h-20 w-full" />
      ) : decidable.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          body="Requests from your managers, and from anybody without a reporting line, land here."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {decidable.slice(0, 6).map((request) => (
            <li key={request.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{request.employee_name}</p>
                <p className="text-xs text-slate-500">
                  {request.leave_type_name} · {formatDate(request.from_date)} → {formatDate(request.to_date)} ·{" "}
                  {request.days}d
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  disabled={pending}
                  onClick={() => onDecide(request.id, "approve")}
                >
                  Approve
                </Button>
                <Button
                  variant="ghost"
                  disabled={pending}
                  onClick={() => onDecide(request.id, "reject")}
                >
                  Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!loading && rows.length > decidable.length ? (
        <p className="mt-2 text-xs text-slate-500">
          {rows.length - decidable.length} more pending request
          {rows.length - decidable.length === 1 ? " is" : "s are"} with the reporting manager.
        </p>
      ) : null}
    </Card>
  );
}

/**
 * First-run setup, driven by the account's real data.
 *
 * A step counts as done only when the underlying record actually exists, so the
 * checklist can never claim progress that was not saved. It disappears once the
 * owner marks setup complete (persisted on the account), and stays hidden on
 * every later login. Steps that belong to a paid module are only shown when the
 * plan includes it; otherwise the owner sees one honest upgrade step.
 */
function SetupChecklist({ employees }: { employees: EmployeeFull[] }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const accountId = user?.account?.id;

  const account = useQuery({
    queryKey: ["account", accountId],
    queryFn: ({ signal }) => api.account(accountId!, signal),
    enabled: Boolean(accountId),
  });
  const usage = useQuery({
    queryKey: ["account-usage", accountId],
    queryFn: ({ signal }) => api.usage(accountId!, signal),
    enabled: Boolean(accountId),
  });
  const peopleEnabled = usage.data?.modules.includes("people") ?? false;

  const departments = useQuery({
    queryKey: ["setup-departments"],
    queryFn: ({ signal }) => api.departmentsPage({ page_size: 1 }, signal),
    enabled: peopleEnabled,
  });
  const branches = useQuery({
    queryKey: ["setup-branches"],
    queryFn: ({ signal }) => api.branches(signal),
    enabled: peopleEnabled,
  });
  const leaveTypes = useQuery({
    queryKey: ["setup-leave-types"],
    queryFn: ({ signal }) => api.leaveTypesPage({ page_size: 1 }, signal),
    enabled: peopleEnabled,
  });

  const complete = useMutation({
    mutationFn: () => api.completeOnboarding(accountId!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["account", accountId] }),
  });

  // Nothing to show once the owner has finished (and the data is loaded).
  if (!account.data || account.data.onboarding_state === "completed") return null;

  const teamCount = employees.filter((e) => e.status !== "exited").length;
  const ownerEmployee = employees.find((e) => e.is_account_owner);

  type SetupStep = {
    key: string;
    label: string;
    body: string;
    done: boolean;
    optional?: boolean;
    to: string;
  };

  // Required steps gate "Mark setup complete"; optional steps never block it.
  // The owner step is satisfied by the provisioned owner employment record, so
  // a freshly created account already shows real progress without inventing it.
  const steps: SetupStep[] = [
    {
      key: "company",
      label: "Confirm your business details",
      body: "Legal name, contact and tax details used on invoices.",
      done: Boolean(account.data.legal_name),
      to: "/settings",
    },
    {
      key: "owner",
      label: "Your owner profile",
      body: "Your employment record, at the root of the organization.",
      done: Boolean(ownerEmployee),
      to: ownerEmployee ? `/people/employees/${ownerEmployee.id}` : "/people/employees",
    },
  ];

  if (peopleEnabled) {
    steps.push(
      {
        key: "departments",
        label: "Set up departments",
        body: "Optional — group the team for reporting and payroll.",
        done: (departments.data?.results.length ?? 0) > 0,
        optional: true,
        to: "/people/organization",
      },
      {
        key: "team",
        label: "Add your first team member",
        body: "Optional if you work alone. The owner record does not count as a team member.",
        done: teamCount > 1,
        optional: true,
        to: "/people/employees/new",
      },
      {
        key: "branches",
        label: "Add a work location",
        body: "Optional — a branch is where attendance is recorded.",
        done: (branches.data?.length ?? 0) > 0,
        optional: true,
        to: "/people/organization",
      },
      {
        key: "leave",
        label: "Review leave types",
        body: "Optional — the leave you grant, and the balances behind it.",
        done: (leaveTypes.data?.results.length ?? 0) > 0,
        optional: true,
        to: "/people/leave?tab=types",
      },
    );
  }

  const doneCount = steps.filter((step) => step.done).length;
  const requiredRemaining = steps.filter((step) => !step.optional && !step.done).length;
  const canComplete = requiredRemaining === 0;

  return (
    <Card className="mb-6">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Let&apos;s build your business</h2>
          <p className="text-xs text-slate-500">
            Welcome, {user?.first_name ?? "there"} — set up {account.data.name} in a few steps.
          </p>
          <p className="text-xs text-slate-500">
            {doneCount} of {steps.length} done
            {requiredRemaining
              ? ` · ${requiredRemaining} required step${requiredRemaining === 1 ? "" : "s"} remaining`
              : " · all required steps done"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/setup"
            className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Start guided setup
          </Link>
          <Button
            variant="secondary"
            disabled={complete.isPending || !canComplete}
            title={canComplete ? undefined : "Finish the required steps first"}
            onClick={() => complete.mutate()}
          >
            Mark setup complete
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {steps.map((step) => (
          <li key={step.key}>
            <Link
              to={step.to}
              className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 hover:bg-slate-50"
            >
              {step.done ? (
                <CheckCircle2 size={18} className="shrink-0 text-emerald-600" />
              ) : (
                <Circle size={18} className="shrink-0 text-slate-300" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">
                  {step.label}
                  {step.optional ? (
                    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      Optional
                    </span>
                  ) : null}
                </span>
                <span className="block text-xs text-slate-500">{step.body}</span>
              </span>
              {step.done ? (
                <span className="shrink-0 text-xs font-medium text-emerald-600">Done</span>
              ) : (
                <ArrowRight size={14} className="shrink-0 text-slate-400" />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * One-click entry to every management area an owner needs, so the employee and
 * business side is never buried under the CRM pages. Plain links; no figures, so
 * nothing here can drift from the module it opens.
 */
function QuickLinks() {
  const links: Array<[string, string]> = [
    ["Organisation", "/people/organization"],
    ["Employees", "/people/employees"],
    ["Attendance", "/people/attendance"],
    ["Leave", "/people/leave"],
    ["Roster", "/people/roster"],
    ["Payroll", "/people/payroll"],
    ["Billing", "/billing/dashboard"],
    ["Settings", "/settings"],
  ];
  return (
    <div className="mb-6 flex flex-wrap gap-2">
      {links.map(([label, to]) => (
        <Link
          key={to}
          to={to}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
        >
          {label}
        </Link>
      ))}
    </div>
  );
}

function PayrollPeriodCard({ run, currency }: { run: PayrollRun; currency: string }) {
  const totals = run.totals as Record<string, unknown> | undefined;
  const net = totals?.net_pay as string | undefined;
  const count = totals?.employee_count as number | undefined;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-900">{run.run_number}</p>
        <Badge tone={PAYROLL_TONE[run.status ?? "draft"]}>{PAYROLL_LABEL[run.status ?? "draft"]}</Badge>
      </div>
      <p className="text-xs text-slate-500">
        {formatDate(run.period_start)} → {run.period_end ? formatDate(run.period_end) : "—"}
        {count != null ? ` · ${count} employee${count === 1 ? "" : "s"}` : ""}
      </p>
      {net != null ? (
        <p className="text-lg font-semibold tabular-nums text-slate-900">
          {currency} {Number(net).toLocaleString("en-IN")}
          <span className="ml-1 text-xs font-normal text-slate-400">net</span>
        </p>
      ) : (
        <p className="text-xs text-slate-400">Not calculated yet.</p>
      )}
      <Link
        to="/people/payroll"
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-900 hover:underline"
      >
        Open Payroll <ArrowRight size={13} />
      </Link>
    </div>
  );
}

function Snapshot({
  loading,
  items,
}: {
  loading: boolean;
  items: Array<{
    key: string;
    label: string;
    value: number;
    to: string;
    icon: React.ReactNode;
  }>;
}) {
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {items.map((item) => (
        <Link
          key={item.key}
          to={item.to}
          className="group block rounded-lg border border-slate-200 bg-white p-4 shadow-xs transition hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
        >
          <div className="flex items-center gap-2 text-slate-500">
            {item.icon}
            <span className="block min-h-8 text-xs font-medium uppercase leading-tight tracking-wide">
              {item.label}
            </span>
          </div>
          {loading ? (
            <Skeleton className="mt-2 h-7 w-12" />
          ) : (
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{item.value}</p>
          )}
        </Link>
      ))}
    </div>
  );
}

function WorkforceCard({
  title,
  loading,
  counts,
  total,
}: {
  title: string;
  loading: boolean;
  counts: Array<[string, number]>;
  total: number;
}) {
  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold text-slate-900">{title}</h2>
      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : counts.length === 0 ? (
        <EmptyState title="No employees yet" body="Add employees to see the split." />
      ) : (
        <ul className="space-y-2.5">
          {counts.slice(0, 6).map(([label, count]) => (
            <li key={label}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate text-slate-600">{label}</span>
                <span className="tabular-nums text-slate-900">{count}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-slate-800"
                  style={{ width: `${total ? Math.round((count / total) * 100) : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AttentionRow({
  label,
  detail,
  count,
  to,
}: {
  label: string;
  detail: string;
  count: number;
  to: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="-mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 hover:bg-slate-50"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-800">{label}</span>
          <span className="block text-xs text-slate-500">{detail}</span>
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
            count > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"
          }`}
        >
          {count}
        </span>
      </Link>
    </li>
  );
}

function countBy(
  rows: EmployeeFull[],
  key: (row: EmployeeFull) => string,
): Array<[string, number]> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const value = key(row);
    map.set(value, (map.get(value) ?? 0) + 1);
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

function localDate(timeZone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}
